import { useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import Papa from "papaparse";
import jalaali from "jalaali-js";
import moment from "moment-jalaali";
import momentTimezone from "moment-timezone";
import { API_URLS } from "../../../../urls";

const useMapData = (
  selectedFeature,
  { setShowDownloadButton, setChartKey, setChartDataList, setSelectedOptions }
) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [mountlyData, setMountlyData] = useState(null);
  const [sunrise, setSunrise] = useState(null);
  const [sunset, setSunset] = useState(null);
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [csvData, setCsvData] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasFetchedDataSuccessfully, setHasFetchedDataSuccessfully] =
    useState(false);

  const [spi, setSpi] = useState(null);
  const [spiSeasonal, setSpiSeasonal] = useState(null);

  const convertToGregorian = (jalaliDate) => {
    const [year, month, day] = jalaliDate.split("-").map(Number);
    const { gy, gm, gd } = jalaali.toGregorian(year, month, day);
    return `${gy}-${String(gm).padStart(2, "0")}-${String(gd).padStart(
      2,
      "0"
    )}`;
  };

  const convertDateToPersian = (date) => {
    return moment(date, "YYYY-MM-DD").format("jYYYY/jMM/jDD");
  };

  const convertDateToPersianWithTime = (date) => {
    return moment(date, "YYYY-MM-DD HH:mm").format("jYYYY/jMM/jDD HH:mm");
  };

  const convertToIranTime = (isoDate) => {
    return momentTimezone.tz(isoDate, "Asia/Tehran").format("HH:mm");
  };

  const sourcePathMap = {
    insurance: "bimeh",
    sam: "sameteo",
    station: "synoptic",
  };

  const sourcePath = sourcePathMap[selectedFeature?.sourceFile] || "";

  const handleStartDateSelect = (date) => setStartDate(date);
  const handleEndDateSelect = (date) => setEndDate(date);

  const handleGetData = async () => {
    setChartDataList([]);
    setSelectedOptions([]);
    setHourlyData([]);
    setDailyData([]);
    setMountlyData([]);
    setCsvData("");
    setIsLoading(true);
    setErrorMessage("");
    setHasFetchedDataSuccessfully(false);
    setShowDownloadButton(false);
    setChartKey((prev) => prev + 1);

    if (!selectedLocId) {
      setErrorMessage("loc_id انتخاب نشده است!");
      setIsLoading(false);
      return;
    }

    const accessToken = Cookies.get("access_token");
    const config = {
      method: "get",
      headers: { Authorization: `Bearer ${accessToken}` },
    };

    try {
      const startDateGregorian = convertToGregorian(startDate);
      const endDateGregorian = convertToGregorian(endDate);

      const urlHourly = `${API_URLS.BASE_URL}panel/hourly/${sourcePath}?loc_id=${selectedLocId}&start_date=${startDateGregorian}&end_date=${endDateGregorian}`;
      const urlDaily = `${API_URLS.BASE_URL}panel/daily/${sourcePath}?loc_id=${selectedLocId}&start_date=${startDateGregorian}&end_date=${endDateGregorian}`;
      const urlMountly = `${API_URLS.BASE_URL}panel/monthly?loc_id=${selectedLocId}&start_date=${startDateGregorian}&end_date=${endDateGregorian}`;

      let hourly = [];
      let daily = [];
      let mountly = [];

      if (sourcePath === "synoptic") {
        const dailyRes = await axios.get(urlDaily, config);
        daily = dailyRes.data;
      } else {
        const [hourlyRes, dailyRes, mountlyRes] = await Promise.all([
          axios.get(urlHourly, config),
          axios.get(urlDaily, config),
          axios.get(urlMountly, config),
        ]);
        hourly = hourlyRes.data;
        daily = dailyRes.data;
        mountly = mountlyRes.data;
      }

      setHourlyData(hourly);
      setDailyData(daily);
      setMountlyData(mountly);

      if (mountly.length > 0) {
        setSpi(mountly[0].spi);
        setSpiSeasonal(mountly[0].spi_seasonal);
      } else {
        setSpi(null);
        setSpiSeasonal(null);
      }

      if (hourly.length > 0) {
        setSunrise(hourly[0]?.sunrise_local || null);
        setSunset(hourly[0]?.sunset_local || null);
      } else {
        setSunrise(null);
        setSunset(null);
      }
      const csvHourly = Papa.unparse(hourly);
      const csvDaily = Papa.unparse(daily);
      const csvMountly = Papa.unparse(mountly);
      setCsvData(`${csvHourly}\n\n${csvDaily}\n\n${csvMountly}`);
      setHasFetchedDataSuccessfully(true);
      setShowDownloadButton(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setErrorMessage(
          "دسترسی شما تنها محدود به نقاط مربوط به استان تحت پوشش شماست."
        );
      } else {
        setErrorMessage("خطا در دریافت داده‌ها.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    startDate,
    endDate,
    handleStartDateSelect,
    handleEndDateSelect,
    handleGetData,
    hourlyData,
    dailyData,
    mountlyData,
    sunrise,
    spi,
    spiSeasonal,
    sunset,
    selectedLocId,
    setSelectedLocId,
    csvData,
    isLoading,
    errorMessage,
    setErrorMessage,
    hasFetchedDataSuccessfully,
    convertDateToPersian,
    convertDateToPersianWithTime,
    convertToIranTime,
  };
};

export default useMapData;
